import { Link } from 'react-router-dom';
import { Alert, Card } from '@zarya/ui';
import type { components } from '@app/api/types';
import { dataHour } from '@app/format';
import { t, tn } from '@app/i18n';
import { identityAccessHref } from '@app/navigation';
import { apiOf } from '../../Resources/AgentModel';
import { useResource } from '../../Resources/Resources.api';
import s from '../Identities.module.css';

type IdentityAgentResource = components['schemas']['IdentityAgentResource'];

/**
 * Secção «Recebe A2A» da aba Identidade (T5) — fase 2, aim-co1.3, prancha `Detalhe-identidade` aprovada em 03/10.
 *
 * Só aparece quando um recurso agente está ligado à identidade: é por ele que ela recebe chamadas A2A. Sem nenhum é
 * o caso (b) do modelo — a identidade chama e ninguém a chama —, e a secção não existe. A lista de Identidades (T4)
 * não muda (D11): «recebe A2A» fica só aqui.
 */
export function ReceivesA2A({
  identityId,
  agents,
}: {
  identityId: string;
  agents: IdentityAgentResource[];
}) {
  if (agents.length === 0) return null;
  return (
    <Card title={t('identities.receivesA2A.title')} className={s.cardEmpilhado}>
      {agents.map((agent) => (
        <AgentLine key={agent.resource_id} identityId={identityId} agent={agent} />
      ))}
    </Card>
  );
}

/** Um recurso agente da identidade: o card e quem pode invocar vêm do detalhe do recurso (`getResource`). */
function AgentLine({ identityId, agent }: { identityId: string; agent: IdentityAgentResource }) {
  const resource = useResource(agent.resource_id);
  const detail = resource.data?.agent;
  const path = `/a2a/${agent.path}/`;
  const resourceHref = `/resources?drawer=${agent.resource_id}`;
  const invokers = detail?.invokers?.length ?? null;

  return (
    <div data-testid={`receives-a2a-${agent.path}`}>
      {agent.state === 'no_route' ? (
        <Alert tone="warning" role="status">
          {t('identities.receivesA2A.noRoute', { api: apiOf(agent) })}{' '}
          <Link to={resourceHref}>{t('identities.receivesA2A.noRoute.link')}</Link>
        </Alert>
      ) : null}
      {agent.state === 'untrusted_target' ? (
        <Alert tone="danger" role="status">
          {t('identities.receivesA2A.untrusted', { api: apiOf(agent) })}
        </Alert>
      ) : null}
      <dl className={s.leitura}>
        <dt>{t('identities.receivesA2A.resource')}</dt>
        <dd>
          <Link to={resourceHref} aria-label={t('identities.receivesA2A.resource.open', { name: agent.name })}>
            {agent.name}
          </Link>
        </dd>
        <dt>{t('identities.receivesA2A.path')}</dt>
        <dd className={s.mono}>{path}</dd>
        <dt>{t('identities.receivesA2A.card')}</dt>
        <dd>
          {detail?.card?.protocol_version && detail.card_read_at
            ? t('identities.receivesA2A.card.value', {
                version: detail.card.protocol_version,
                when: dataHour(detail.card_read_at) ?? '—',
              })
            : t('identities.receivesA2A.card.none')}
        </dd>
        <dt>{t('identities.receivesA2A.invokers')}</dt>
        <dd>
          {invokers === null ? (
            '—'
          ) : (
            <Link
              to={identityAccessHref(identityId, 'incoming')}
              aria-label={t('identities.receivesA2A.invokers.open')}
            >
              {tn('identities.receivesA2A.invokers.count', invokers)}
            </Link>
          )}
        </dd>
      </dl>
    </div>
  );
}
